/**
 * Shared helpers for matching CMS `dpat-indicator` rows to legacy DPAT `tableCommented` slugs (e.g. c1.0-1.1).
 */

export function normalizeDpatIndicatorCode(raw) {
  if (!raw) return "";
  let compact = String(raw).trim().replace(/–|—/g, "-").replace(/\s+/g, "");
  if (/^CI/i.test(compact)) compact = compact.replace(/^CI/i, "c");
  else if (/^SDI/i.test(compact)) compact = compact.replace(/^SDI/i, "sdi");
  else if (/^PI/i.test(compact)) compact = compact.replace(/^PI/i, "pi");
  return compact.toLowerCase().replace(/[^a-z0-9.-]/g, "");
}

/**
 * Human-readable code label from legacy slug (e.g. `c1.0-1.1` → `CI 1.0 - 1.1`).
 */
export function formatDpatSlugAsCodeLabel(slug) {
  if (!slug) return "";
  const raw = String(slug).trim().toLowerCase();
  let prefix = "";
  let rest = raw;
  if (raw.startsWith("c") && /^c[\d.]/.test(raw)) {
    prefix = "CI";
    rest = raw.slice(1);
  } else if (raw.startsWith("sdi")) {
    prefix = "SDI";
    rest = raw.slice(3);
  } else if (raw.startsWith("pi")) {
    prefix = "PI";
    rest = raw.slice(2);
  } else {
    return String(slug).trim();
  }
  const dash = rest.indexOf("-");
  if (dash === -1) return `${prefix} ${rest}`.trim();
  const left = rest.slice(0, dash);
  const right = rest.slice(dash + 1);
  return `${prefix} ${left} - ${right}`.trim();
}

export function extractIndicatorListFromCmsPayload(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  const candidates = [
    body.data,
    body.content,
    body.indicators,
    body.data?.content,
    body.data?.indicators,
    body.result,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

/** Coerce year prop (number, string, or select value) for CMS API paths. */
export function resolveDpatCmsYear(year) {
  if (year == null || year === "") return null;
  if (typeof year === "object" && year?.value != null) {
    const nested = Number(year.value);
    return Number.isFinite(nested) ? nested : year.value;
  }
  const n = Number(year);
  return Number.isFinite(n) ? n : year;
}

export function getIndicatorRawCode(indicator) {
  if (!indicator) return "";
  const raw = indicator.code ?? indicator.indicatorCode ?? indicator.indicator_code ?? "";
  return String(raw).trim();
}

/** Strip trailing roman sub-indicator suffix (e.g. pi1.0-1.4iii → pi1.0-1.4). */
export function stripDpatSubIndicatorSuffix(normalized) {
  if (!normalized) return "";
  const match = String(normalized).match(/^(.+\d)([ivxlc]+)$/i);
  return match ? match[1] : normalized;
}

function scoreIndicatorRichness(indicator) {
  const fields = [
    indicator?.thematicAreas,
    indicator?.assessmentGuide,
    indicator?.scoringCriteria,
    indicator?.meansOfVerification,
    indicator?.indicatorName,
  ];
  return fields.filter((f) => typeof f === "string" && f.trim()).length;
}

/**
 * Match a CMS indicator row to a DPAT sheet slug (e.g. pi1.0-1.4).
 * Handles exact codes, parent rows, and sub-part codes (1.4i / 1.4ii / 1.4iii).
 */
export function findCmsIndicatorForSlug(list, slug) {
  if (!slug || !Array.isArray(list) || list.length === 0) return null;

  const target = String(slug).toLowerCase().trim();
  const entries = list.map((ind) => {
    const normalized = normalizeDpatIndicatorCode(getIndicatorRawCode(ind));
    const base = stripDpatSubIndicatorSuffix(normalized);
    return { ind, normalized, base };
  });

  const exact = entries.find((e) => e.normalized === target);
  if (exact) return exact.ind;

  const baseMatches = entries.filter((e) => e.base === target);
  if (baseMatches.length === 1) return baseMatches[0].ind;
  if (baseMatches.length > 1) {
    const parent = baseMatches.find((e) => e.normalized === e.base);
    if (parent) return parent.ind;
    return baseMatches.sort(
      (a, b) => scoreIndicatorRichness(b.ind) - scoreIndicatorRichness(a.ind),
    )[0].ind;
  }

  return null;
}

export function resolveThematicAreasText(indicator) {
  if (!indicator) return "";
  if (typeof indicator.thematicAreas === "string" && indicator.thematicAreas.trim()) {
    return indicator.thematicAreas.trim();
  }
  if (indicator.thematicArea?.thematicAreaName) {
    return String(indicator.thematicArea.thematicAreaName).trim();
  }
  if (indicator.thematicAreaName) return String(indicator.thematicAreaName).trim();
  return "";
}
